import { BigquerySavedQuery, BigquerySharedQuery } from "lib/Api";
import * as monaco from "monaco-editor";
import * as React from "react";
import { createContext, useContext, useState } from "react";
import { FormStore, GraphFormStore, QueryResult } from "../store";

type ReceiverPayloads = [
  {
    receiver: BigquerySavedQuery;
    payload: "deleted";
  },
  {
    receiver: BigquerySharedQuery;
    payload: "deleted";
  },
  {
    receiver: FormStore;
    payload: "focus" | monaco.editor.IMarkerData;
  },
  {
    receiver: GraphFormStore;
    payload: "focus" | monaco.editor.IMarkerData;
  },
  {
    receiver: QueryResult;
    payload: "focus";
  },
];

type Filter<T, V> = T extends [infer Head, ...infer Rest]
  ? Head extends { receiver: infer Receiver; payload: infer Payload }
    ? V extends Receiver
      ? Receiver extends V
        ? Payload
        : Filter<Rest, V>
      : Filter<Rest, V>
    : Filter<Rest, V>
  : never;

type LookupPayload<Type> = Filter<ReceiverPayloads, Type>;

interface Context {
  subscriptions: Subscription[];
}

interface Subscription {
  receiver: unknown;
  callbacks: ((payload: unknown) => void)[];
}

interface Publisher {
  publish<T>(receiver: T, payload: LookupPayload<T>): void;
}

interface Subscriber {
  subscribe<T>(
    receiver: T,
    callback: (payload: LookupPayload<T>) => void,
  ): monaco.IDisposable;
}

const pubsubContext = createContext<Context>({
  subscriptions: [],
});

export function usePublisher(): Publisher {
  const context = useContext(pubsubContext);
  return {
    // @ts-expect-error TS2322
    publish: <T,>(receiver: T, payload: LookupPayload<T>) => {
      const subscription = context.subscriptions.find(
        (_) => _.receiver == receiver,
      );
      if (!subscription) {
        return;
      }
      for (const callback of subscription.callbacks) {
        callback(payload);
      }
    },
  };
}

export function useSubscriber(): Subscriber {
  const context = useContext(pubsubContext);
  return {
    subscribe: <T,>(
      receiver: T,
      callback: (payload: LookupPayload<T>) => void,
    ): monaco.IDisposable => {
      let subscribtion = context.subscriptions.find(
        (_) => _.receiver == receiver,
      );
      if (!subscribtion) {
        subscribtion = {
          receiver,
          callbacks: [],
        };
        context.subscriptions.push(subscribtion);
      }
      // @ts-expect-error TS2322
      subscribtion.callbacks.push(callback);
      return {
        dispose: () => {
          // @ts-expect-error TS2322
          const index = subscribtion.callbacks.indexOf(callback);
          if (index >= 0) {
            subscribtion.callbacks.splice(index, 1);
          }
        },
      };
    },
  };
}

interface PubsubContextProviderProps {
  children: React.ReactNode;
}

export function PubsubContextProvider(props: PubsubContextProviderProps) {
  const { children } = props;
  const [value, _] = useState<Context>({ subscriptions: [] });

  return (
    <pubsubContext.Provider value={value}>{children}</pubsubContext.Provider>
  );
}
